// Editing a shape's vertices: insert and delete on any ring-based shape (a
// polygon, a wall or line, a room through its polygon shim), in the world or in
// a part's local frame, keeping the outer ring's constraints in step; and the
// panels of a selected edge or corner. docs/claude/rooms.md.
import {
  type ObjectConstraint,
  type Polygon,
  type ProductInstance,
  type RevisionCloud,
  type Vec2,
  type Wall,
} from '../../shared/types';
import {
  MULTI_KINDS,
  selectedCount,
  selectOne,
} from '../selection';
import {
  enterEdit,
  flashStatus,
  flashToolStatus,
  invalidate,
  newId,
  pickRoomCorner,
  pushHistory,
  renderProperties,
  state,
  vp,
} from './core';
import { cornerAngle } from '../gcs';
import {
  distance,
  instanceToWorld,
  pointInPolygon,
  polyBenchLength,
  projectToSegment,
} from '../geometry';
import { tr } from '../i18n';
import {
  renumberOnInsert,
  renumberOnRemove,
  renumberOnRingRemove,
} from '../objConstraints';
import {
  infoRow,
  numberRow,
  sectionTitle,
} from './panelRows';
import {
  cornerRadiusRaw,
  effectiveCornerRadius,
  GLASS_MIN,
  insertVertexEdgeFeatures,
  isJoint,
  maxCornerRadius,
  polyEdgeLengths,
  removeVertexEdgeFeatures,
  rescaleEdgeFeatures,
  segToWall,
  setWallLengthPin,
  type VertexRemap,
  wallChain,
  wallLengthPin,
  wallRuns,
} from '../polyRoom';
import {
  addRoomRelation,
  appendConstraintStatus,
  findVertRelation,
  lengthLockButton,
  makePolyEdgeAxis,
  makeRoomEdgeAxis,
  polyRingCons,
  reconcileEdgeLength,
  removePolyRelation,
  removeRoomRelation,
  renderEdgeConstraintsList,
  renderRelationButtons,
  resizePolyShapeEdge,
  resizePolyWall,
  roomRingCons,
  setRoomVertexRelValue,
  updateEdgeLenArray,
  vertexLockButton,
} from './ringConstraints';
import { toPlan } from './view3d';
import { followRing } from '../ringRenumber';
import {
  polylineIsClosed,
  wallBand,
} from '../wallTool';
import {
  edgeConstraintOf,
  formatLen,
  HANDLE_PX,
  regenerateSauna,
  setEdgeConstraint,
} from '../main';
import { polyContains } from '../hitRules';
import {
  followVertices,
  insertRemap,
  removeRemap,
} from '../anchors';
import { roomDoors } from '../door';
import { shiftNodeFrameSwaps } from '../benchSolids';

/** A shape whose vertices can be edited in vertex-edit mode: user polygons and
 *  walls, plus revision clouds (a closed ring like a polygon, with no holes and
 *  no edge constraints — the polygon-specific paths below all no-op for them). */
export type EditableShape = Polygon | Wall | RevisionCloud;

/** The polygon/wall currently in vertex-edit mode, or null. */
export function editingVertexShape(): EditableShape | null {
  if (!state.editVerticesId) return null;
  return (
    (state.drawing.polygons || []).find((p) => p.id === state.editVerticesId) ||
    (state.drawing.walls || []).find((w) => w.id === state.editVerticesId) ||
    (state.drawing.revisionClouds || []).find((c) => c.id === state.editVerticesId) ||
    null
  );
}

/** Edit a polygon's, a wall's / line's or a revision cloud's vertices: what a
 *  double-click on it, or Edit polygon on the Draw toolbar, does. */
export function enterShapeVertexEdit(shape: EditableShape): void {
  toPlan();
  enterEdit({ kind: 'shape', id: shape.id });
  const kind = (state.drawing.revisionClouds || []).some((c) => c.id === shape.id) ? 'cloud'
    : (state.drawing.polygons || []).some((p) => p.id === shape.id) ? 'polygon' : 'wall';
  selectOne(state, kind, shape.id);
  renderProperties();
  invalidate();
}

/** The one selected object whose vertices can be edited (an ellipse has none). */
export function selectedEditableShape(): EditableShape | null {
  if (selectedCount(state, MULTI_KINDS) !== 1) return null;
  const poly = state.selectedPolyId ? (state.drawing.polygons || []).find((p) => p.id === state.selectedPolyId) : null;
  if (poly) return poly.ellipse ? null : poly;
  const wall = state.selectedWallId ? (state.drawing.walls || []).find((w) => w.id === state.selectedWallId) : null;
  if (wall) return wall;
  return state.selectedRevCloudId ? (state.drawing.revisionClouds || []).find((c) => c.id === state.selectedRevCloudId) ?? null : null;
}

export function isPolygonShape(shape: EditableShape): boolean {
  return !('thickness' in shape);
}

/** All editable rings of a shape: ring 0 is the outer ring, rings 1+ polygon holes. */
export function shapeRings(shape: EditableShape): Vec2[][] {
  return isPolygonShape(shape) ? [shape.points, ...((shape as Polygon).holes || [])] : [shape.points];
}

/** Replace ring `r` of a shape (0 = outer, 1+ = holes). */
export function setShapeRing(shape: EditableShape, r: number, pts: Vec2[]): void {
  if (r === 0) { shape.points = pts; return; }
  const holes = (shape as Polygon).holes;
  if (holes) holes[r - 1] = pts;
}

/** Drop hole ring `r` (1+) from a polygon; no-op for the outer ring. */
function removeShapeRing(shape: EditableShape, r: number): void {
  if (r === 0 || !isPolygonShape(shape)) return;
  const poly = shape as Polygon;
  poly.holes = (poly.holes || []).filter((_, i) => i !== r - 1);
  if (!poly.holes.length) poly.holes = undefined;
}

/** The ring+index of the vertex under a screen point, searching every ring, or null. */
export function vertexAtScreenRings(shape: EditableShape, sp: Vec2): { ring: number; index: number } | null {
  const rings = shapeRings(shape);
  for (let r = 0; r < rings.length; r++) {
    const idx = vertexAtScreen(rings[r], sp);
    if (idx >= 0) return { ring: r, index: idx };
  }
  return null;
}

/** The nearest segment across all rings of a shape to a world point, within `tol`. */
export function nearestSegmentRings(shape: EditableShape, world: Vec2, tol: number): { ring: number; index: number; point: Vec2 } | null {
  const rings = shapeRings(shape);
  let best: { ring: number; index: number; point: Vec2 } | null = null;
  let bestD = tol;
  for (let r = 0; r < rings.length; r++) {
    // The outer ring of a polygon and every hole are closed; a wall is an open polyline.
    const closed = isPolygonShape(shape) || r > 0;
    const seg = nearestSegment(rings[r], world, closed, tol);
    if (seg) {
      const d = distance(world, seg.point);
      if (d < bestD) { bestD = d; best = { ring: r, index: seg.index, point: seg.point }; }
    }
  }
  return best;
}

/** Every vertex of a shape except one (ring, index) — for square-up snapping. */
export function otherRingVertices(shape: EditableShape, ring: number, index: number): Vec2[] {
  const out: Vec2[] = [];
  shapeRings(shape).forEach((r, ri) => r.forEach((v, vi) => { if (!(ri === ring && vi === index)) out.push(v); }));
  return out;
}

/** Whether a world point lies within a shape's drawn area (outside polygon holes). */
export function pointInShape(shape: EditableShape, world: Vec2): boolean {
  if (isPolygonShape(shape)) return polyContains(shape as Polygon, world);
  const outline = wallBand(shape.points, (shape as Wall).thickness, (shape as Wall).align);
  return outline.length >= 3 && pointInPolygon(world, outline);
}

/** Index of the vertex whose screen position is under `sp`, or -1. */
export function vertexAtScreen(points: Vec2[], sp: Vec2): number {
  for (let i = 0; i < points.length; i++) {
    const s = vp.worldToScreen(points[i]);
    if (Math.hypot(s.x - sp.x, s.y - sp.y) <= HANDLE_PX) return i;
  }
  return -1;
}

/** Project a point onto a segment, returning the closest point on it. */
/** The nearest segment of a shape to a world point, within `tol`, with the split point. */
export function nearestSegment(points: Vec2[], world: Vec2, closed: boolean, tol: number): { index: number; point: Vec2 } | null {
  let best: { index: number; point: Vec2 } | null = null;
  let bestD = tol;
  const segs = closed ? points.length : points.length - 1;
  for (let i = 0; i < segs; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const proj = projectToSegment(world, a, b);
    const d = distance(world, proj);
    if (d <= bestD) {
      bestD = d;
      best = { index: i, point: proj };
    }
  }
  return best;
}

/** Insert `pt` after `afterIndex` on ring `ring`; what is numbered by the outer
 *  ring's vertices follows (`changeOuterRing`). */
export function insertVertexInShape(shape: EditableShape, ring: number, afterIndex: number, pt: Vec2): void {
  if (!isRoomShim(shape)) renumberObjConstraints((cs) => renumberOnInsert(cs, shape.id, ringBase(shape, ring) + afterIndex));
  const r = shapeRings(shape)[ring];
  const put = (): VertexRemap => {
    r.splice(afterIndex + 1, 0, pt);
    setShapeRing(shape, ring, r);
    return insertRemap(afterIndex);
  };
  if (ring === 0) changeOuterRing(shape, put); else put();
}

/** Change a shape's outer ring so that everything numbered by its vertices
 *  follows: in the drawing, through `followVertices` (anchors.ts) — what is
 *  anchored to it and its own locks, pins, relations and doors / panes /
 *  openings; a part's own shape, or a room's outline seen as a polygon (its
 *  anchors follow at the room's own call site), through `followRing`. */
function changeOuterRing(shape: EditableShape, change: () => VertexRemap): void {
  const inDrawing = (state.drawing.polygons ?? []).includes(shape as Polygon)
    || (state.drawing.walls ?? []).includes(shape as Wall)
    || (state.drawing.revisionClouds ?? []).includes(shape as RevisionCloud)
    || state.drawing.instances.some((i) => i.id === shape.id && !i.polygon && i.nodes === shape.points);
  if (inDrawing) followVertices(state.drawing, shape.id, change);
  else followRing(shape as Polygon & Wall, isPolygonShape(shape), change);
}

/** The index the first vertex of ring `ring` has in a constraint's numbering. */
function ringBase(shape: EditableShape, ring: number): number {
  return shapeRings(shape).slice(0, ring).reduce((s, r) => s + r.length, 0);
}
/** A room's polygon seen as a shape (`polyRoomShim`): its constraints count walls. */
function isRoomShim(shape: EditableShape): boolean {
  return state.drawing.instances.some((i) => i.id === shape.id && !!i.polygon);
}
export function renumberObjConstraints(f: (cs: ObjectConstraint[]) => ObjectConstraint[]): void {
  if (state.drawing.constraints?.length) state.drawing.constraints = f(state.drawing.constraints);
}

/**
 * Whether a vertex can be removed from ring `ring` without dropping below a valid
 * shape. The outer ring keeps 2 pts for a wall/open path, 3 for a closed polygon;
 * a hole vertex is always removable (a hole at the triangle limit is dropped whole).
 */
export function canRemoveVertex(shape: EditableShape, ring: number): boolean {
  if (ring > 0) return true;
  const min = isPolygonShape(shape) ? 3 : 2;
  return shapeRings(shape)[ring].length > min;
}

/**
 * Remove vertex `index` from ring `ring`; what is numbered by the outer ring's
 * vertices follows (`changeOuterRing`; an anchor on the vertex removed is let go
 * where it is). A hole that would drop below a triangle is dropped whole. Assumes
 * `canRemoveVertex` was checked; a no-op otherwise.
 */
export function removeVertexFromShape(shape: EditableShape, ring: number, index: number): void {
  const r = shapeRings(shape)[ring];
  // A closed wall / line's joint is two stacked points, removed as one corner.
  const joint = !isPolygonShape(shape) && ring === 0 && polylineIsClosed(r) && (index === 0 || index === r.length - 1);
  if (joint) index = 0;
  // Constraints between objects are renumbered here too (a room's by WALL, at its own call site).
  const base = ringBase(shape, ring);
  if (!isRoomShim(shape) && (ring > 0 || canRemoveVertex(shape, ring))) {
    renumberObjConstraints((cs) => (ring > 0 && r.length <= 3
      ? renumberOnRingRemove(cs, shape.id, base, r.length)
      : renumberOnRemove(cs, shape.id, base + index, { base, len: r.length, closed: isPolygonShape(shape) })));
  }
  if (ring > 0) {
    if (r.length <= 3) { removeShapeRing(shape, ring); return; }
    r.splice(index, 1);
    setShapeRing(shape, ring, r);
    return;
  }
  if (!canRemoveVertex(shape, ring)) return;
  const n = r.length;
  const take = (): VertexRemap => {
    r.splice(index, 1);
    if (joint) {
      r.pop();                                      // its twin
      if (r.length >= 3) r.push({ ...r[0] });       // still a ring, joined at the next corner
    }
    setShapeRing(shape, ring, r);
    return joint ? (v) => (v === 0 || v === n - 1 ? null : v - 1) : removeRemap(index);
  };
  changeOuterRing(shape, take);
}

/**
 * A `Polygon` view of a poly-room's local `polygon` + its `polygonConstraints`,
 * so the shared edge-constraint helpers apply to rooms too. `points` is the same
 * array (mutations write through); after any call that may reassign the lock
 * array, copy `shim.edgeConstraints` back with `inst.polygonConstraints = ...`.
 */
export function polyRoomShim(inst: ProductInstance): Polygon {
  return { id: inst.id, points: inst.polygon ?? [], edgeConstraints: inst.polygonConstraints, edgeLengths: inst.polygonEdgeLengths, relations: inst.polygonRelations } as Polygon;
}

/** Re-centre a poly-room's local polygon on the instance origin and refresh its
 *  width/depth, moving the instance so the room stays put in world space. This
 *  keeps the bounding box (drawn centred at inst.x/inst.y ± width/depth) tracking
 *  the reshaped outline instead of drifting when a vertex is dragged outward. */
export function recenterPolyRoom(inst: ProductInstance): void {
  const pts = inst.polygon;
  if (!pts || !pts.length) return;
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  if (cx !== 0 || cy !== 0) {
    const off = instanceToWorld(inst, { x: cx, y: cy }); // world point the new origin should sit at
    inst.polygon = pts.map((p) => ({ x: p.x - cx, y: p.y - cy }));
    inst.x = off.x; inst.y = off.y;
  }
  inst.width = maxX - minX;
  inst.depth = maxY - minY;
}

/** Copy a poly-room shim's constraint arrays back onto the instance — call after
 *  any helper that reshapes them (vertex insert/delete). */
/** Keep the per-CORNER arrays (rounding radius, corner slat direction) lined up
 *  with `polygon` when vertex-edit inserts or deletes a corner. Without this
 *  every corner after the edit inherits its neighbour's radius. */
export function spliceCornerArrays(inst: ProductInstance, at: number, op: 'insert' | 'remove'): void {
  const n = inst.polygon?.length ?? 0;
  const fix = <T>(arr: T[] | undefined, blank: T): T[] | undefined => {
    if (!arr) return undefined;
    const out = arr.slice();
    if (op === 'insert') out.splice(at, 0, blank); else out.splice(at, 1);
    while (out.length < n) out.push(blank);
    out.length = n;
    return out.some((v) => v != null && v !== 0) ? out : undefined;
  };
  inst.polygonRadii = fix(inst.polygonRadii, null);
  inst.polygonCornerSlats = fix(inst.polygonCornerSlats, null);
  inst.polygonJoints = fix(inst.polygonJoints, null); // an inserted vertex is a corner
  if (inst.polygonJoints && !inst.polygonJoints.some(Boolean)) inst.polygonJoints = undefined;
}

export function writeBackPolyRoomShim(inst: ProductInstance, sh: Polygon): void {
  inst.polygonConstraints = sh.edgeConstraints;
  inst.polygonEdgeLengths = sh.edgeLengths;
  inst.polygonRelations = sh.relations;
}

/**
 * A `Wall` view of an instance's node polyline, so the shared vertex helpers edit
 * it as an open, unconstrained ring. `points` is the same array (mutations write
 * through); the `thickness` marker just makes `isPolygonShape` treat it as a wall.
 */
export function nodeShim(inst: ProductInstance): Wall {
  return { id: inst.id, points: inst.nodes ?? [], thickness: 0 } as Wall;
}

/** Put a corner into room `inst`'s outline on segment `seg`, at `at` (its local
 *  frame) — one undo step. Everything numbered by vertex, segment or wall follows:
 *  the constraints between objects, the corner arrays, every edge-hosted feature
 *  and what is anchored to the corners (anchors.ts). Refused inside a pane or an
 *  opening, which owns its whole segment. */
export function insertRoomCorner(inst: ProductInstance, seg: number, at: Vec2): void {
  const poly = inst.polygon;
  if (!poly) return;
  if ([...(inst.glass || []), ...(inst.openings || [])].some((g) => g.innerWallId == null && g.edge === seg)) {
    flashStatus(tr('A corner can’t go inside a pane or opening — move or delete it first.'));
    return;
  }
  pushHistory();
  // Bench spans are measured along the OUTER edge line, so the split point's
  // distance from the edge start splits them exactly — measured before the ring changes.
  const edgeA = poly[seg], edgeB = poly[(seg + 1) % poly.length];
  const splitAt = Math.hypot(at.x - edgeA.x, at.y - edgeA.y);
  const edgeLen = Math.hypot(edgeB.x - edgeA.x, edgeB.y - edgeA.y);
  // Benches, heater walls and partitions count WALLS: where on its wall the corner lands.
  const onWall = segToWall(inst, seg);
  const wallSplit = onWall ? { wall: onWall.wall, at: onWall.offset + splitAt, len: wallRuns(inst)[onWall.wall].len } : undefined;
  if (onWall) renumberObjConstraints((cs) => renumberOnInsert(cs, inst.id, onWall.wall));
  followVertices(state.drawing, inst.id, () => {
    const sh = polyRoomShim(inst);
    insertVertexInShape(sh, 0, seg, at);
    writeBackPolyRoomShim(inst, sh);
    return insertRemap(seg);
  });
  spliceCornerArrays(inst, seg + 1, 'insert'); // the new corner starts sharp
  // Every edge-hosted feature is re-indexed in one place — benches, heater walls,
  // glass, openings, partitions and doors. Handling only some of them is how a
  // partition on a later wall used to jump edges and go zero-thick.
  insertVertexEdgeFeatures(inst, seg, splitAt, edgeLen, (kind) => newId(kind), wallSplit);
  regenerateSauna(inst);
  invalidate();
}

/** Take corner `v` out of room `inst`'s outline — one undo step, with the same
 *  follow-ups as `insertRoomCorner` (an anchor on the corner falls back to its
 *  stored point). A jamb goes with its pane (refused); a corner beside jambs
 *  merges its two walls into one straight wall (`removeCornerBesideJambs`). */
export function removeRoomCorner(inst: ProductInstance, v: number): void {
  if (!inst.polygon || !canRemoveVertex(polyRoomShim(inst), 0)) return;
  if (isJoint(inst, v)) {
    flashStatus(tr('A jamb goes with its pane — delete the pane or opening instead.'));
    return;
  }
  if (removeCornerBesideJambs(inst, v)) { regenerateSauna(inst); invalidate(); return; }
  pushHistory();
  const goneWall = wallRuns(inst).findIndex((r) => r.a === v);
  if (goneWall >= 0) {
    const walls = wallRuns(inst).length;
    renumberObjConstraints((cs) => renumberOnRemove(cs, inst.id, goneWall, { base: 0, len: walls, closed: true }));
  }
  followVertices(state.drawing, inst.id, () => {
    const sh = polyRoomShim(inst);
    removeVertexFromShape(sh, 0, v);
    writeBackPolyRoomShim(inst, sh);
    return removeRemap(v);
  });
  // Corner-indexed arrays lose the removed corner's entry, so every later corner
  // keeps its own radius instead of inheriting its neighbour's.
  spliceCornerArrays(inst, v, 'remove');
  pickRoomCorner(null);
  // Removing vertex `v` drops its outgoing edge and shifts every later edge down by
  // one; the mirror of the insert re-indexes every edge-hosted feature so each stays
  // on its own wall (or goes with the wall that went).
  removeVertexEdgeFeatures(inst, v, goneWall);
  regenerateSauna(inst);
  invalidate();
}

/**
 * Delete room corner `v` when either wall meeting there holds jambs: the two walls
 * become ONE straight wall from the corner before `v` to the corner after it, and
 * every figure on them is re-laid along it in proportion (pinned ones and doors
 * holding), so the panes stay on a straight wall instead of hanging off the two old
 * lines. A pane / opening on the wall that started at `v` goes with that wall, as
 * any feature on a removed edge does. Refused (with a status message) when the
 * held figures no longer fit. Returns false when neither wall has a jamb — the
 * plain corner delete then runs as it always did.
 */
function removeCornerBesideJambs(inst: ProductInstance, v: number): boolean {
  const runs = wallRuns(inst);
  const w = runs.findIndex((r) => r.a === v);
  if (w < 0) return false;
  const prevW = (w - 1 + runs.length) % runs.length;
  if (runs[w].segs.length === 1 && runs[prevW].segs.length === 1) return false;
  const n = inst.polygon!.length, prevSeg = (v - 1 + n) % n;
  const oldLens = polyEdgeLengths(inst);
  const oldPts = inst.polygon!.map((p) => ({ ...p }));
  const oldJoints = inst.polygon!.map((_, i) => isJoint(inst, i));
  // Rehearse on a copy, so a refusal leaves the room — and the undo stack — alone.
  const probe = structuredClone(inst);
  const sh = polyRoomShim(probe);
  removeVertexFromShape(sh, 0, v);
  writeBackPolyRoomShim(probe, sh);
  spliceCornerArrays(probe, v, 'remove');
  removeVertexEdgeFeatures(probe, v, w);
  const shift = (i: number): number => (i > v ? i - 1 : i);
  // Lay the merged wall out as it stood: the segment that ran into `v` now runs on
  // to the next vertex, so its old length is both halves'.
  const lens = oldLens.filter((_, i) => i !== v);
  const merged = shift(prevSeg);
  lens[merged] = oldLens[prevSeg] + oldLens[v];
  for (const d of roomDoors(probe)) if (d.innerWallId == null && (d.edge ?? 0) === merged) d.offset = (d.offset || 0) - oldLens[v] / 2;
  const mw = wallRuns(probe).findIndex((r) => r.a === shift(runs[prevW].a));
  if (mw < 0) return false;
  const held = wallChain(probe, mw, { segLens: lens }).reduce((a, sp) =>
    a + (sp.locked || sp.kind === 'door' ? sp.t - sp.s : sp.kind === 'solid' ? 0 : GLASS_MIN), 0);
  if (held > wallRuns(probe)[mw].len + 0.5) {
    flashStatus(tr('The pinned figures on these two walls don’t fit on one straight wall.'));
    return true;
  }
  rescaleEdgeFeatures(probe, lens);
  pushHistory();
  const walls = runs.length;
  renumberObjConstraints((cs) => renumberOnRemove(cs, inst.id, w, { base: 0, len: walls, closed: true }));
  followVertices(state.drawing, inst.id, () => {
    for (const k of Object.keys(inst) as Array<keyof ProductInstance>) if (!(k in probe)) delete inst[k];
    Object.assign(inst, probe);
    // Corners never move here, so an anchor on one finds it again by position.
    const newPts = inst.polygon!;
    return (i) => {
      if (i === v || oldJoints[i]) return null;
      const k = newPts.findIndex((p) => Math.abs(p.x - oldPts[i].x) < 1e-6 && Math.abs(p.y - oldPts[i].y) < 1e-6);
      return k >= 0 ? k : null;
    };
  });
  pickRoomCorner(null);
  return true;
}


/** Put a node into a node run (`inst.nodes`: a salt wall, a heated bench) after node
 *  `after` — one undo step; its length and its centre follow. */
export function insertNode(inst: ProductInstance, after: number, pt: Vec2): void {
  pushHistory();
  insertVertexInShape(nodeShim(inst), 0, after, pt);
  shiftNodeFrameSwaps(inst, after, 1);
  settleNodeRun(inst);
  invalidate();
}

/** Take node `index` out of a node run — one undo step (a run keeps 2 nodes). */
export function removeNode(inst: ProductInstance, index: number): void {
  if (!canRemoveVertex(nodeShim(inst), 0)) return;
  pushHistory();
  removeVertexFromShape(nodeShim(inst), 0, index);
  shiftNodeFrameSwaps(inst, index, -1);
  settleNodeRun(inst);
  invalidate();
}

/** A node run's length and its centre (its nodes' average), after its nodes changed. */
export function settleNodeRun(inst: ProductInstance): void {
  const nodes = inst.nodes ?? [];
  if (!nodes.length) return;
  inst.x = nodes.reduce((sum, n) => sum + n.x, 0) / nodes.length;
  inst.y = nodes.reduce((sum, n) => sum + n.y, 0) / nodes.length;
  inst.width = Math.round(polyBenchLength(nodes));
}

/** Properties for a selected polygon edge: length, make-horizontal / make-vertical. */
export function renderEdgeProperties(body: HTMLElement, poly: Polygon, edge: number): void {
  const n = poly.points.length;
  const A = poly.points[edge], B = poly.points[(edge + 1) % n];
  const len = Math.hypot(B.x - A.x, B.y - A.y);
  body.appendChild(sectionTitle(`${tr('Edge')} ${edge + 1} / ${n}`));
  body.appendChild(infoRow('Length', formatLen(len)));

  // Typing a new length resizes the edge the same way its face dimension and a
  // snapped dimension-line do (resizePolyShapeEdge carries aligned vertices, so a
  // rectangle stays a rectangle), keeping all three edit paths in agreement.
  // When the edge's length is locked, typing updates the lock value too.
  const lenLocked = (poly.edgeLengths?.[edge] ?? null) != null;
  if (lenLocked && poly.edgeLengths) poly.edgeLengths = reconcileEdgeLength(poly.edgeLengths, edge, len); // keep the stored lock in lockstep with the geometry
  const lenRow = numberRow('Length (mm)', Math.round(len), (v) => {
    if (v <= 0) return;
    if (lenLocked) poly.edgeLengths = updateEdgeLenArray(poly.edgeLengths, n, edge, v);
    if (resizePolyShapeEdge(poly, edge, (edge + 1) % n, v)) { renderProperties(); invalidate(); }
  }, { commit: true });
  body.appendChild(lenRow);
  body.appendChild(lengthLockButton(lenLocked, () => {
    pushHistory();
    poly.edgeLengths = updateEdgeLenArray(poly.edgeLengths, n, edge, lenLocked ? null : Math.round(len));
    renderProperties(); invalidate();
  }));

  // Make horizontal / vertical: solve the whole polygon so every other lock,
  // length and relation stays satisfied. Pressed when already locked to that
  // axis; clicking a pressed one clears the lock.
  const con = edgeConstraintOf(poly, edge);
  const grid = document.createElement('div');
  grid.style.display = 'grid';
  grid.style.gridTemplateColumns = '1fr 1fr';
  grid.style.gap = '4px';
  grid.style.margin = '6px 0';
  const mk = (label: string, axis: 'h' | 'v'): HTMLButtonElement => {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.fontSize = '12px';
    b.style.padding = '6px 4px';
    const pressed = con === axis;
    b.className = 'toggle' + (pressed ? ' active' : '');
    b.addEventListener('click', () => {
      if (pressed) { pushHistory(); setEdgeConstraint(poly, edge, null); }
      else if (!makePolyEdgeAxis(poly, edge, axis)) { flashToolStatus(tr('Can’t make segment {a} {axis} — it would conflict with the existing constraints.').replace('{a}', String(edge + 1)).replace('{axis}', tr(axis === 'h' ? 'horizontal' : 'vertical'))); return; }
      renderProperties(); invalidate();
    });
    return b;
  };
  grid.append(mk(tr('Make horizontal'), 'h'), mk(tr('Make vertical'), 'v'));
  body.appendChild(grid);
  renderRelationButtons(body, poly.id, edge);
  renderEdgeConstraintsList(body, poly.id, edge, polyRingCons(poly), {
    orient: () => { pushHistory(); setEdgeConstraint(poly, edge, null); renderProperties(); invalidate(); },
    length: () => { pushHistory(); poly.edgeLengths = updateEdgeLenArray(poly.edgeLengths, n, edge, null); renderProperties(); invalidate(); },
    relation: (i) => removePolyRelation(poly, i),
  });
  appendConstraintStatus(body, poly.points, polyRingCons(poly));
}

/** Properties for a selected room wall: length + horizontal/vertical lock. */
export function renderPolyRoomEdgeProperties(body: HTMLElement, inst: ProductInstance, edge: number): void {
  // The clicked segment belongs to a WALL (a pane's jambs split it); the title,
  // length and length lock are the wall's. Orientation and relations act on the
  // segment, which the solver keeps collinear with the rest of its wall.
  const runs = wallRuns(inst);
  const w = segToWall(inst, edge)?.wall ?? edge;
  body.appendChild(sectionTitle(`${tr('Wall')} ${w + 1} / ${runs.length}`));
  const wallLen = runs[w]?.len ?? 0;
  const lenLocked = wallLengthPin(inst, w) != null;
  if (lenLocked && inst.polygonEdgeLengths && runs[w]?.segs.length === 1) inst.polygonEdgeLengths = reconcileEdgeLength(inst.polygonEdgeLengths, edge, wallLen); // keep the stored lock in lockstep with the geometry
  body.appendChild(numberRow('Length (mm)', Math.round(wallLen), (v) => {
    if (v <= 0) return;
    if (lenLocked) setWallLengthPin(inst, w, v);
    if (resizePolyWall(inst, w, v)) { regenerateSauna(inst); renderProperties(); invalidate(); }
  }, { commit: true }));
  body.appendChild(lengthLockButton(lenLocked, () => {
    pushHistory();
    setWallLengthPin(inst, w, lenLocked ? null : Math.round(wallLen));
    renderProperties(); invalidate();
  }));
  // Make horizontal / vertical: solve the whole room ring so every other lock,
  // length and relation stays satisfied. Pressed = already on that axis.
  const con = edgeConstraintOf(polyRoomShim(inst), edge);
  const grid = document.createElement('div');
  grid.style.display = 'grid';
  grid.style.gridTemplateColumns = '1fr 1fr';
  grid.style.gap = '4px';
  grid.style.margin = '6px 0';
  const mk = (label: string, axis: 'h' | 'v'): HTMLButtonElement => {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.fontSize = '12px';
    b.style.padding = '6px 4px';
    const pressed = con === axis;
    b.className = 'toggle' + (pressed ? ' active' : '');
    b.addEventListener('click', () => {
      if (pressed) { pushHistory(); const sh = polyRoomShim(inst); setEdgeConstraint(sh, edge, null); inst.polygonConstraints = sh.edgeConstraints; regenerateSauna(inst); }
      else if (!makeRoomEdgeAxis(inst, edge, axis)) { flashToolStatus(tr('Can’t make segment {a} {axis} — it would conflict with the existing constraints.').replace('{a}', String(edge + 1)).replace('{axis}', tr(axis === 'h' ? 'horizontal' : 'vertical'))); return; }
      renderProperties(); invalidate();
    });
    return b;
  };
  grid.append(mk(tr('Make horizontal'), 'h'), mk(tr('Make vertical'), 'v'));
  body.appendChild(grid);
  renderRelationButtons(body, inst.id, edge);
  renderEdgeConstraintsList(body, inst.id, edge, roomRingCons(inst), {
    orient: () => { pushHistory(); const sh = polyRoomShim(inst); setEdgeConstraint(sh, edge, null); inst.polygonConstraints = sh.edgeConstraints; regenerateSauna(inst); renderProperties(); invalidate(); },
    length: () => { pushHistory(); setWallLengthPin(inst, w, null); regenerateSauna(inst); renderProperties(); invalidate(); },
    relation: (i) => removeRoomRelation(inst, i),
  });
  appendConstraintStatus(body, inst.polygon!, roomRingCons(inst));
}

/**
 * The properties panel for a selected poly-room corner: its angle (with an
 * angle-lock toggle) and its local X/Y position (each with a coordinate lock).
 * The locks are stored as vertex relations on the ring and maintained by the
 * constraint solver, so a locked corner holds its angle / position while other
 * corners are dragged.
 */
export function renderPolyRoomVertexProperties(body: HTMLElement, inst: ProductInstance, vertex: number): void {
  const pts = inst.polygon!;
  const n = pts.length;
  const P = pts[vertex];
  body.appendChild(sectionTitle(`${tr('Corner')} ${vertex + 1} / ${n}`));
  const rels = inst.polygonRelations;

  // Corner angle lock (angleV keeps the signed corner angle — sign is winding).
  const ang = cornerAngle(pts, vertex);
  if (ang != null) {
    const angIdx = findVertRelation(rels, 'angleV', vertex);
    const angLocked = angIdx >= 0;
    body.appendChild(numberRow('Angle (°)', Math.round(Math.abs(ang) * 10) / 10, (v) => {
      if (v <= 0 || v >= 180) return;
      const signed = Math.sign(ang || 1) * v;
      if (angLocked) setRoomVertexRelValue(inst, angIdx, signed);
      else addRoomRelation(inst, { type: 'angleV', vert: vertex, value: signed });
    }, { commit: true }));
    body.appendChild(vertexLockButton(angLocked, tr('Angle'), () => {
      if (angLocked) removeRoomRelation(inst, angIdx);
      else addRoomRelation(inst, { type: 'angleV', vert: vertex, value: ang });
    }));
  }

  // X / Y coordinate locks (local frame; value pins the coordinate absolutely).
  const coordRow = (label: 'X' | 'Y', get: () => number, type: 'lockX' | 'lockY'): void => {
    const idx = findVertRelation(rels, type, vertex);
    const locked = idx >= 0;
    body.appendChild(numberRow(`${label} (mm)`, Math.round(get()), (v) => {
      if (locked) setRoomVertexRelValue(inst, idx, v);
      else addRoomRelation(inst, { type, vert: vertex, value: v });
    }, { commit: true }));
    body.appendChild(vertexLockButton(locked, tr(label === 'X' ? 'Lock X' : 'Lock Y'), () => {
      if (locked) removeRoomRelation(inst, idx);
      else addRoomRelation(inst, { type, vert: vertex, value: get() });
    }));
  };
  coordRow('X', () => P.x, 'lockX');
  coordRow('Y', () => P.y, 'lockY');

  // Rounded corner: the radius is measured on the OUTER wall face; the inner
  // face and every bench ring follow it concentrically, so the wall keeps its
  // thickness through the curve. Typing is clamped to what the walls and their
  // features can give — the stored value survives, so shrinking a wall tightens
  // the curve and undoing lets it spring back.
  body.appendChild(sectionTitle('Rounded corner'));
  const eff = effectiveCornerRadius(inst, vertex);
  const stored = cornerRadiusRaw(inst, vertex);
  body.appendChild(numberRow('Radius (mm)', Math.round(stored), (v) => {
    pushHistory();
    const arr = (inst.polygonRadii ?? []).slice();
    while (arr.length < n) arr.push(null);
    arr[vertex] = Math.max(0, Math.min(Math.round(v), Math.round(maxCornerRadius(inst, vertex))));
    inst.polygonRadii = arr.some((r) => (r ?? 0) > 0) ? arr : undefined;
    regenerateSauna(inst);
    renderProperties();
    invalidate();
  }, { commit: true }));
  if (stored > 0 && Math.abs(eff - stored) > 1) {
    body.appendChild(infoRow('In use', tr('{n} mm — limited by this corner’s walls').replace('{n}', String(Math.round(eff)))));
  }
  if (eff > 0) {
    // Slat direction inside the bench pieces that fill this corner.
    const slatRow = document.createElement('div');
    slatRow.className = 'prop-row';
    const slatLab = document.createElement('label');
    slatLab.textContent = tr('Corner slats');
    const slatSel = document.createElement('select');
    for (const [val, txt] of [['concentric', 'Concentric'], ['radial', 'Radial']] as const) {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = tr(txt);
      slatSel.appendChild(opt);
    }
    slatSel.value = inst.polygonCornerSlats?.[vertex] === 'radial' ? 'radial' : 'concentric';
    slatSel.addEventListener('change', () => {
      pushHistory();
      const arr = (inst.polygonCornerSlats ?? []).slice();
      while (arr.length < n) arr.push(null);
      arr[vertex] = slatSel.value === 'radial' ? 'radial' : null;
      inst.polygonCornerSlats = arr.some((s) => s) ? arr : undefined;
      invalidate();
    });
    slatRow.append(slatLab, slatSel);
    body.appendChild(slatRow);
  }

  appendConstraintStatus(body, pts, roomRingCons(inst));
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.style.marginTop = '8px';
  hint.textContent = tr('Lock this corner’s angle or X/Y so it holds while you drag the other corners. Set a radius to round it. Click a wall or blank area to deselect.');
  body.appendChild(hint);
}
